import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Table,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Container,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  TableSortLabel,
} from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import HandymanIcon from "@mui/icons-material/Handyman";
import BuildIcon from "@mui/icons-material/Build";
import { Add, Close, Search } from "@mui/icons-material";

import { financeApi } from "./apiService";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import {
  commonTextFieldProps,
  smallButtonProp,
  tableHeaderStyles,
  tableRowStyles,
} from "../../utils/styles";
import { formatCurrency, formatText } from "../../utils/utils";
import PaymentModal from "./components/PaymentModal";
import NavigationHeader from "../../components/NavigationHeader";
import { Loading } from "../../components/Loading";
import CustomChip from "../../components/CustomChip";
import {
  expensePartyTypeConfig,
  getFilteredPartyCategories,
} from "./components/PartySummaryCards";
const PartyDetails = () => {
  const { partyType } = useParams<{ partyType: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { planStatus } = usePlanStatus();

  // Get filtered party categories based on operation type
  const partyCategories = getFilteredPartyCategories(
    "expense",
    planStatus?.operationType
  );

  const [isLoading, setIsLoading] = useState(true);
  const [partyListWithBalance, setPartyListWithBalance] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState<any>(null);

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedPartyType, setSelectedPartyType] = useState<string>("");

  // Get party config
  const config = partyType
    ? expensePartyTypeConfig[partyType as keyof typeof expensePartyTypeConfig]
    : null;

  // Enhanced filtering and sorting logic
  const getFilteredAndSortedParties = () => {
    let filtered = (partyListWithBalance || []).filter((party: any) => {
      // Search filter
      if (searchQuery.trim()) {
        const searchTerm = searchQuery.toLowerCase().trim();
        const partyName = party?.name?.toLowerCase() || "";
        const partyPhone = party?.phoneNumber?.toLowerCase() || "";
        const partyTypeName = party?.type?.toLowerCase() || "";

        const matchesSearch =
          partyName.includes(searchTerm) ||
          partyPhone.includes(searchTerm) ||
          partyTypeName.includes(searchTerm);
        if (!matchesSearch) return false;
      }

      return true;
    });

    // Sorting
    if (sortBy) {
      filtered.sort((a: any, b: any) => {
        let aValue: any;
        let bValue: any;

        switch (sortBy) {
          case "name":
            aValue = a?.name?.toLowerCase() || "";
            bValue = b?.name?.toLowerCase() || "";
            break;
          case "transactions":
            aValue = a?.totalTransactions || 0;
            bValue = b?.totalTransactions || 0;
            break;
          case "due":
            aValue = a?.totalDue || 0;
            bValue = b?.totalDue || 0;
            break;
          case "paid":
            aValue = a?.totalPaid || 0;
            bValue = b?.totalPaid || 0;
            break;
          case "balance":
            aValue = a?.totalBalance || 0;
            bValue = b?.totalBalance || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  const filteredParties = getFilteredAndSortedParties();

  const getPartyListWithBalance = async (partyType: string) => {
    try {
      setIsLoading(true);
      const response = await financeApi.getPartyListWithBalance(
        partyType,
        "expense"
      );

      // Extract party list and summary from response
      if (response && typeof response === "object") {
        // If response has summary and partyList structure (new API format)
        if (response.summary && response.partyList) {
          setPartyListWithBalance(response.partyList);
          setSummary(response.summary);
        }
        // If response has summary and parties structure
        else if (response.summary && response.parties) {
          setPartyListWithBalance(response.parties);
          setSummary(response.summary);
        }
        // If response has summary property directly
        else if (response.summary) {
          setPartyListWithBalance(response.partyList || response.parties || []);
          setSummary(response.summary);
        }
        // Fallback: treat as array of parties (existing behavior)
        else {
          setPartyListWithBalance(Array.isArray(response) ? response : []);
        }
      } else {
        setPartyListWithBalance(response || []);
      }
    } catch (error) {
      console.error("Error fetching party list with balance:", error);
      setPartyListWithBalance([]);
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (planStatus?.company?.id && partyType) {
      getPartyListWithBalance(partyType);
    } else {
      setIsLoading(false);
    }
  }, [partyType, planStatus]);

  // Initialize selectedPartyType from URL param on mount
  useEffect(() => {
    if (partyType) {
      setSelectedPartyType(partyType);
    }
  }, [partyType]);

  // Sorting and filtering handlers
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSortBy("");
    setSortOrder("asc");
  };

  // Transaction Functions
  const handleShowTransactions = (party: any) => {
    setSelectedParty(party);
    setPaymentModalOpen(true);
  };

  const handleCloseModal = () => {
    setPaymentModalOpen(false);
    setSelectedParty(null);
  };

  const handleTransactionAdded = async () => {
    // Refresh party list when a transaction is added
    if (partyType) {
      await getPartyListWithBalance(partyType);
    }
  };

  // Handle party type dropdown change
  const handlePartyChange = (event: any) => {
    const newPartyType = event.target.value;
    setSelectedPartyType(newPartyType);

    // Navigate to the new party type URL
    if (newPartyType) {
      navigate(`/finance/expense/party/${newPartyType}`);
    }
  };

  if (isLoading) {
    return <Loading message="Loading party details..." fullScreen={true} />;
  }

  if (!partyType || !config) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="error">
          Invalid party type
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/finance/expense")}
          sx={{ mt: 2 }}
        >
          Back to Finance
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title={
          partyType
            ? `${config?.title || formatText(partyType)} - Payables`
            : "Payables - Party Details"
        }
        backNavigationEnabled={true}
        backNavigationTo="/finance/expense"
        breadcrumbs={[
          { label: "Finance", path: "/finance" },
          { label: "Payables", path: "/finance/expense" },
          {
            label: partyType
              ? config?.title || formatText(partyType)
              : "Party Details",
          },
        ]}
      />

      {/* Party Selection Dropdown */}
      <Box sx={{ mb: 2 }}>
        <FormControl
          fullWidth
          size="small"
          sx={{ maxWidth: 400, fontSize: "0.75rem" }}
        >
          <InputLabel id="party-select-label" sx={{ fontSize: "0.75rem" }}>
            Select Party Type
          </InputLabel>
          <Select
            labelId="party-select-label"
            id="party-select"
            value={selectedPartyType || ""}
            label="Select Party Type"
            onChange={handlePartyChange}
            {...commonTextFieldProps.SelectProps}
          >
            {partyCategories.map((category) => (
              <MenuItem key={category.id} value={category.value}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {category.title}
                  </Typography>
                  <CustomChip value={category.financialType} />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Show message when no party type is selected */}
      {!partyType && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Select a Party Type
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please select a party type from the dropdown above to view payables
              details.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Party Summary Card */}
      {partyType && summary && config && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 0.5, py: 0.5, "&:last-child": { pb: 0.5 } }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}
            >
              {/* Icon and Title */}
              <Box
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  borderRadius: "50%",
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(config.icon, { sx: { fontSize: 16 } })}
              </Box>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ minWidth: 100, fontSize: "0.9rem" }}
              >
                {config.title}
              </Typography>

              {/* All Stats in one row */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flex: 1,
                  justifyContent: "space-around",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {summary.totalParties}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem" }}
                  >
                    Parties
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {summary.totalTransactions}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem" }}
                  >
                    Transactions
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {formatCurrency(summary.totalDue)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem" }}
                  >
                    Payable
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {formatCurrency(summary.totalPaid)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem" }}
                  >
                    Paid
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {formatCurrency(summary.totalBalance)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem" }}
                  >
                    Balance
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Party List */}
      {partyType && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      {formatText(selectedPartyType)} Transactions
                    </Typography>
                    {/* Search Bar */}
                    <Box
                      sx={{
                        mb: 2,
                        width: "30%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Search by name, phone, or type"
                        placeholder="Enter party name, phone number, or type..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        {...commonTextFieldProps}
                        InputProps={{
                          endAdornment: searchQuery.length > 0 && (
                            <InputAdornment position="end">
                              <Close
                                style={{ fontSize: "1rem", cursor: "pointer" }}
                                onClick={() => setSearchQuery("")}
                              />
                            </InputAdornment>
                          ),
                          startAdornment: (
                            <Search
                              style={{ fontSize: "1rem", marginRight: 2 }}
                            />
                          ),
                        }}
                      />
                      {searchQuery && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            fontSize: "0.7rem",
                            textAlign: "right",
                          }}
                        >
                          {filteredParties.length} of{" "}
                          {partyListWithBalance.length} parties found
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Active Filters Display */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    {(searchQuery || sortBy) && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Filters:
                        </Typography>
                        {searchQuery && (
                          <Chip
                            label={`Search: ${searchQuery}`}
                            size="small"
                            onDelete={() => setSearchQuery("")}
                            variant="outlined"
                          />
                        )}
                        {sortBy && (
                          <Chip
                            label={`Sort: ${sortBy} ${sortOrder}`}
                            size="small"
                            onDelete={() => {
                              setSortBy("");
                              setSortOrder("asc");
                            }}
                            variant="outlined"
                          />
                        )}
                        <Button
                          size="small"
                          onClick={clearFilters}
                          sx={{ fontSize: "0.7rem" }}
                        >
                          Clear All
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>

                <TableContainer>
                  <Table
                    size="small"
                    aria-label="party list table"
                    sx={{
                      tableLayout: "auto",
                      "& .MuiTableCell-root": {
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {partyType === "staff" && (
                          <TableCell sx={tableHeaderStyles}>
                            Party Type
                          </TableCell>
                        )}
                        <TableCell sx={tableHeaderStyles}>
                          <TableSortLabel
                            active={sortBy === "name"}
                            direction={sortBy === "name" ? sortOrder : "asc"}
                            onClick={() => handleSortChange("name")}
                          >
                            Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>Phone</TableCell>
                        <TableCell sx={tableHeaderStyles}>
                          <TableSortLabel
                            active={sortBy === "transactions"}
                            direction={
                              sortBy === "transactions" ? sortOrder : "asc"
                            }
                            onClick={() => handleSortChange("transactions")}
                          >
                            Transactions
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>
                          <TableSortLabel
                            active={sortBy === "due"}
                            direction={sortBy === "due" ? sortOrder : "asc"}
                            onClick={() => handleSortChange("due")}
                          >
                            Payable
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>
                          <TableSortLabel
                            active={sortBy === "paid"}
                            direction={sortBy === "paid" ? sortOrder : "asc"}
                            onClick={() => handleSortChange("paid")}
                          >
                            Paid
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>
                          <TableSortLabel
                            active={sortBy === "balance"}
                            direction={sortBy === "balance" ? sortOrder : "asc"}
                            onClick={() => handleSortChange("balance")}
                          >
                            Balance
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredParties.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{
                              textAlign: "center",
                              py: 4,
                              fontSize: "0.75rem",
                            }}
                          >
                            {searchQuery ? (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1 }}
                                >
                                  No parties found matching "{searchQuery}"
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => setSearchQuery("")}
                                  sx={{ fontSize: "0.7rem" }}
                                >
                                  Clear Search
                                </Button>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No parties available for this category
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredParties.map((party) => (
                          <TableRow key={party?.id}>
                            {partyType === "staff" && (
                              <TableCell sx={tableRowStyles}>
                                {party?.type}
                              </TableCell>
                            )}
                            <TableCell sx={tableRowStyles}>
                              {party?.name}
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              {party?.phoneNumber || "-"}
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              {party?.totalTransactions || 0}
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              {formatCurrency(party?.totalDue || 0)}
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              {formatCurrency(party?.totalPaid || 0)}
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              {formatCurrency(party?.totalBalance || 0)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  sx={smallButtonProp.sx}
                                  startIcon={
                                    <Add style={{ fontSize: "1rem" }} />
                                  }
                                  onClick={() => handleShowTransactions(party)}
                                >
                                  Payment
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  sx={smallButtonProp.sx}
                                  onClick={() =>
                                    navigate(
                                      `/finance/transactions/${party?.type}/${party?.id}`,
                                      {
                                        state: {
                                          party: party,
                                          partyType: partyType,
                                        },
                                      }
                                    )
                                  }
                                >
                                  Show Transactions
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Transaction Modal */}
      {partyType && (
        <PaymentModal
          open={paymentModalOpen}
          onClose={handleCloseModal}
          selectedParty={selectedParty}
          onTransactionAdded={handleTransactionAdded}
        />
      )}
    </Container>
  );
};

export default PartyDetails;
